
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { Check, X, ArrowUpRight, Package, ChevronDown, ChevronUp, Building2 } from 'lucide-react';

// Helper to format role names
const formatRole = (role) => {
    if (!role) return '—';
    return role
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// Plain text status pill: "<Label> Approved" / "<Label> Pending". Fixed width so it
// lines up with the action button grouped directly underneath it.
const StatusChip = ({ label, approved }) => (
    <span className={`w-40 text-center px-2 py-1 rounded text-xs font-medium ${
        approved ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
    }`}>
        {label} {approved ? 'Approved' : 'Pending'}
    </span>
);

export default function AdminUserDetail() {
    const { id } = useParams();
    const [user, setUser] = useState(null);
    const [keys, setKeys] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [eventCategories, setEventCategories] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [savingEvents, setSavingEvents] = useState(false);
    
    // User-level writeback events (like webhooks)
    const [selectedWritebackEvents, setSelectedWritebackEvents] = useState([]);
    const [savingWritebackEvents, setSavingWritebackEvents] = useState(false);
    
    // User apps (for display only)
    const [userApps, setUserApps] = useState([]);

    // Dental offices permissions (from Legacy API office list)
    const [officeList, setOfficeList] = useState([]);
    const [officeListError, setOfficeListError] = useState(null);
    const [selectedOffices, setSelectedOffices] = useState([]);
    const [officeSearchTerm, setOfficeSearchTerm] = useState('');
    const [officePage, setOfficePage] = useState(1);
    const [savingOffices, setSavingOffices] = useState(false);
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const OFFICES_PER_PAGE = 10;

    const fetchData = async () => {
        try {
            const [userRes, keyRes, logRes, eventsRes, appsRes, officeListRes] = await Promise.all([
                api.get(`/api/admin/users/${id}`),
                api.get(`/api/admin/users/${id}/keys`),
                api.get(`/api/admin/users/${id}/logs`),
                api.get(`/api/admin/event-types/all`),
                api.get(`/api/admin/users/${id}/apps`),
                api.get(`/api/admin/users/${id}/office-list`).catch((err) => {
                    console.error(err);
                    setOfficeListError('Failed to load dental offices permissions');
                    return null;
                })
            ]);

            setUser(userRes.data);
            setKeys(keyRes.data);
            setLogs(logRes.data);
            setEventCategories(eventsRes.data.categories);
            setSelectedEvents(userRes.data.allowed_event_types || []);
            setSelectedWritebackEvents(userRes.data.allowed_writeback_events || []);
            setUserApps(appsRes.data || []);
            setSelectedOffices(
                (userRes.data.allowed_dental_offices || []).map(entry => entry.split(',')[0])
            );

            if (officeListRes) {
                const rawData = officeListRes.data?.data;
                const offices = Array.isArray(rawData)
                    ? rawData
                    : Array.isArray(rawData?.offices)
                        ? rawData.offices
                        : Array.isArray(rawData?.data)
                            ? rawData.data
                            : [];
                setOfficeList(offices);
            }
        } catch (err) {
            console.error(err);
            alert('Failed to load user details');
        } finally {
            setLoading(false);
        }
    };

    const rotateKey = async (keyId) => {
        if (!window.confirm('Are you sure you want to rotate this API key?')) return;

        try {
            await api.post(`/api/admin/api-keys/${keyId}/rotate`);
            alert('Key rotated successfully');
            fetchData();
        } catch {
            alert('Failed to rotate the key');
        }
    };

    const toggleEvents = async () => {
        const approved = user.events_approved;
        if (approved && !window.confirm("Revoke this user's access to writeback events and event types?")) return;
        try {
            await api.post(
                approved ? '/api/revoke-user-events' : '/api/approve-user-events',
                { email: user.email }
            );
            fetchData();
        } catch {
            alert(`Failed to ${approved ? 'revoke' : 'approve'} events access`);
        }
    };

    const toggleCredential = async (credentialType) => {
        const approved = credentialType === 'sandbox'
            ? user.sandbox_credentials_approved
            : user.live_credentials_approved;
        const label = credentialType === 'sandbox' ? 'Sandbox (Staging)' : 'Production (Live)';
        if (approved && !window.confirm(`Revoke ${label} credentials access for this user?`)) return;
        try {
            await api.post(
                approved ? '/api/revoke-user-credentials' : '/api/approve-user-credentials',
                { email: user.email, credential_type: credentialType }
            );
            fetchData();
        } catch {
            alert(`Failed to ${approved ? 'revoke' : 'approve'} ${label} credentials access`);
        }
    };

    const approveKey = async (keyId) => {
        try {
            await api.post(`/api/admin/api-keys/${keyId}/approve`);
            alert('API key approved');
            fetchData();
        } catch {
            alert('Failed to approve the API key');
        }
    };

    const revokeKey = async (keyId) => {
        if (!window.confirm('Are you sure you want to revoke this API key?')) return;

        try {
            await api.post(`/api/admin/api-keys/${keyId}/revoke`);
            alert('API key revoked');
            fetchData();
        } catch {
            alert('Failed to revoke the API key');
        }
    };

    const toggleEvent = (eventName) => {
        setSelectedEvents(prev => {
            if (prev.includes(eventName)) {
                return prev.filter(e => e !== eventName);
            } else {
                return [...prev, eventName];
            }
        });
    };

    const toggleCategory = (categoryEvents) => {
        const allSelected = categoryEvents.every(e => selectedEvents.includes(e));

        if (allSelected) {
            // Deselect all in category
            setSelectedEvents(prev => prev.filter(e => !categoryEvents.includes(e)));
        } else {
            // Select all in category
            setSelectedEvents(prev => {
                const newSet = new Set([...prev, ...categoryEvents]);
                return Array.from(newSet);
            });
        }
    };

    const selectAllEvents = () => {
        const allEvents = eventCategories.flatMap(cat => cat.events);
        setSelectedEvents(allEvents);
    };

    const deselectAllEvents = () => {
        setSelectedEvents([]);
    };

    const saveEventPermissions = async () => {
        setSavingEvents(true);
        try {
            await api.post(`/api/admin/users/${id}/event-permissions`, {
                allowed_event_types: selectedEvents
            });
            alert('Event permissions updated successfully!');
            fetchData();
        } catch (err) {
            console.error(err);
            alert('Failed to update event permissions');
        } finally {
            setSavingEvents(false);
        }
    };

    // User-level writeback event functions
    const toggleWritebackEvent = (eventName) => {
        setSelectedWritebackEvents(prev => {
            if (prev.includes(eventName)) {
                return prev.filter(e => e !== eventName);
            } else {
                return [...prev, eventName];
            }
        });
    };

    const toggleWritebackCategory = (categoryEvents) => {
        const allSelected = categoryEvents.every(e => selectedWritebackEvents.includes(e));

        if (allSelected) {
            setSelectedWritebackEvents(prev => prev.filter(e => !categoryEvents.includes(e)));
        } else {
            setSelectedWritebackEvents(prev => {
                const newSet = new Set([...prev, ...categoryEvents]);
                return Array.from(newSet);
            });
        }
    };

    const selectAllWritebackEvents = () => {
        // Hide patient.record.deleted event for writeback functionality
        const allEvents = eventCategories.flatMap(cat => cat.events).filter(e => e !== 'patient.record.deleted');
        setSelectedWritebackEvents(allEvents);
    };

    const deselectAllWritebackEvents = () => {
        setSelectedWritebackEvents([]);
    };

    const saveWritebackPermissions = async () => {
        setSavingWritebackEvents(true);
        try {
            await api.post(`/api/admin/users/${id}/writeback-permissions`, {
                allowed_writeback_events: selectedWritebackEvents
            });
            alert('Writeback permissions updated successfully!');
            fetchData();
        } catch (err) {
            console.error(err);
            alert('Failed to update writeback permissions');
        } finally {
            setSavingWritebackEvents(false);
        }
    };

    const toggleOfficeSelection = (officeId) => {
        const id = String(officeId);
        setSelectedOffices(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const selectAllOffices = () => {
        const allIds = filteredOffices.map(o => String(o.dentulu_office_id));
        setSelectedOffices(prev => Array.from(new Set([...prev, ...allIds])));
    };

    const deselectAllOffices = () => {
        setSelectedOffices([]);
    };

    const saveDentalOfficePermissions = async () => {
        setSavingOffices(true);
        try {
            const offices = officeList
                .filter(o => selectedOffices.includes(String(o.dentulu_office_id)))
                .map(o => ({
                    dentulu_office_id: o.dentulu_office_id,
                    email: o.email,
                    office_name: o.office_name,
                    mobile: o.mobile,
                    administrator_name: o.administrator_name
                }));
            await api.post(`/api/admin/users/${id}/dental-offices`, { offices });
            alert('Dental office permissions updated successfully!');
            fetchData();
        } catch (err) {
            console.error(err);
            alert('Failed to update dental office permissions');
        } finally {
            setSavingOffices(false);
        }
    };

    const filteredOffices = officeList
        .filter((office) => {
            if (!showSelectedOnly) return true;
            return selectedOffices.includes(String(office.dentulu_office_id));
        })
        .filter((office) => {
            const term = officeSearchTerm.toLowerCase();
            if (!term) return true;
            return (
                String(office.dentulu_office_id ?? '').toLowerCase().includes(term) ||
                String(office.email ?? '').toLowerCase().includes(term) ||
                String(office.office_name ?? '').toLowerCase().includes(term) ||
                String(office.mobile ?? '').toLowerCase().includes(term) ||
                String(office.administrator_name ?? '').toLowerCase().includes(term)
            );
        });

    const officeTotalPages = Math.ceil(filteredOffices.length / OFFICES_PER_PAGE) || 1;
    const paginatedOffices = filteredOffices.slice(
        (officePage - 1) * OFFICES_PER_PAGE,
        officePage * OFFICES_PER_PAGE
    );

    useEffect(() => {
        setOfficePage(1);
    }, [officeSearchTerm, showSelectedOnly]);

    useEffect(() => {
        fetchData();
    }, [id]);

    if (loading) return (
        <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );

    if (!user) return <p className="text-center text-gray-600 mt-8">User not found.</p>;

    return (
        <div className="space-y-8 p-6">
            {/* User Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-start justify-between">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900">{user.full_name}</h2>
                        <p className="text-gray-600 mt-1">Email: {user.email}</p>
                        <p className="text-gray-600">Role: <span className="font-semibold">{formatRole(user.role)}</span></p>
                        {user.company && <p className="text-gray-600">Company: {user.company}</p>}
                    </div>

                    <div className="flex gap-4">
                        <div className="flex flex-col items-center gap-2">
                            <StatusChip label="Events" approved={user.events_approved} />
                            <button
                                onClick={toggleEvents}
                                className={`w-40 text-center px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                    user.events_approved
                                        ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {user.events_approved ? 'Revoke Events' : 'Approve Events'}
                            </button>
                        </div>

                        <div className="flex flex-col items-center gap-2">
                            <StatusChip label="Sandbox" approved={user.sandbox_credentials_approved} />
                            <button
                                onClick={() => toggleCredential('sandbox')}
                                className={`w-40 text-center px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                    user.sandbox_credentials_approved
                                        ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                {user.sandbox_credentials_approved ? 'Revoke Sandbox' : 'Approve Sandbox'}
                            </button>
                        </div>

                        <div className="flex flex-col items-center gap-2">
                            <StatusChip label="Live" approved={user.live_credentials_approved} />
                            <button
                                onClick={() => toggleCredential('live')}
                                className={`w-40 text-center px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                                    user.live_credentials_approved
                                        ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                {user.live_credentials_approved ? 'Revoke Live' : 'Approve Live'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Event Permissions Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-semibold text-gray-900">Event Type Permissions</h3>
                    <div className="flex gap-2">
                        <button
                            onClick={selectAllEvents}
                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
                        >
                            Select All
                        </button>
                        <button
                            onClick={deselectAllEvents}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                        >
                            Deselect All
                        </button>
                    </div>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                    Select which event types this user can subscribe to in their webhook configurations.
                    Selected: <span className="font-semibold">{selectedEvents.length}</span> events
                </p>

                <div className="space-y-4">
                    {eventCategories.map((category, idx) => {
                        const categoryEvents = category.events;
                        const selectedCount = categoryEvents.filter(e => selectedEvents.includes(e)).length;
                        const allSelected = categoryEvents.length > 0 && selectedCount === categoryEvents.length;

                        return (
                            <div key={idx} className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <button
                                        onClick={() => toggleCategory(categoryEvents)}
                                        className="flex items-center gap-2 text-lg font-semibold text-gray-800 hover:text-blue-600 transition"
                                    >
                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${allSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                            }`}>
                                            {allSelected && <Check className="w-4 h-4 text-white" />}
                                        </div>
                                        {category.name}
                                    </button>
                                    <span className="text-sm text-gray-500">
                                        {selectedCount} / {categoryEvents.length} selected
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 ml-7">
                                    {categoryEvents.map(event => (
                                        <button
                                            key={event}
                                            onClick={() => toggleEvent(event)}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${selectedEvents.includes(event)
                                                    ? 'bg-blue-50 border-2 border-blue-500 text-blue-700'
                                                    : 'bg-gray-50 border-2 border-gray-200 text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${selectedEvents.includes(event)
                                                    ? 'bg-blue-600 border-blue-600'
                                                    : 'border-gray-300'
                                                }`}>
                                                {selectedEvents.includes(event) && (
                                                    <Check className="w-3 h-3 text-white" />
                                                )}
                                            </div>
                                            <span className="truncate">{event}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button
                    onClick={saveEventPermissions}
                    disabled={savingEvents}
                    className="mt-6 w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                    {savingEvents ? 'Saving...' : 'Save Event Permissions'}
                </button>
            </div>

            {/* Writeback Event Permissions Section (User Level) */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                        <ArrowUpRight className="w-6 h-6 text-orange-600" />
                        Writeback Event Permissions
                    </h3>
                    <div className="flex gap-2">
                        <button
                            onClick={selectAllWritebackEvents}
                            className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition"
                        >
                            Select All
                        </button>
                        <button
                            onClick={deselectAllWritebackEvents}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                        >
                            Deselect All
                        </button>
                    </div>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                    Select which event types this user's apps can <strong>write back</strong> to the ecosystem.
                    Selected: <span className="font-semibold text-orange-600">{selectedWritebackEvents.length}</span> events
                </p>

                <div className="space-y-4">
                    {eventCategories.map((category, idx) => {
                        // Hide patient.record.deleted event for writeback functionality
                        const categoryEvents = category.events.filter(e => e !== 'patient.record.deleted');
                        if (categoryEvents.length === 0) return null;
                        const selectedCount = categoryEvents.filter(e => selectedWritebackEvents.includes(e)).length;
                        const allSelected = categoryEvents.length > 0 && selectedCount === categoryEvents.length;

                        return (
                            <div key={idx} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                                <div className="flex items-center justify-between mb-3">
                                    <button
                                        onClick={() => toggleWritebackCategory(categoryEvents)}
                                        className="flex items-center gap-2 text-lg font-semibold text-gray-800 hover:text-orange-600 transition"
                                    >
                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${allSelected ? 'bg-orange-600 border-orange-600' : 'border-gray-300'
                                            }`}>
                                            {allSelected && <Check className="w-4 h-4 text-white" />}
                                        </div>
                                        {category.name}
                                    </button>
                                    <span className="text-sm text-gray-500">
                                        {selectedCount} / {categoryEvents.length} selected
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 ml-7">
                                    {categoryEvents.map(event => (
                                        <button
                                            key={event}
                                            onClick={() => toggleWritebackEvent(event)}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${selectedWritebackEvents.includes(event)
                                                    ? 'bg-orange-100 border-2 border-orange-500 text-orange-700'
                                                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${selectedWritebackEvents.includes(event)
                                                    ? 'bg-orange-600 border-orange-600'
                                                    : 'border-gray-300'
                                                }`}>
                                                {selectedWritebackEvents.includes(event) && (
                                                    <Check className="w-3 h-3 text-white" />
                                                )}
                                            </div>
                                            <span className="truncate">{event}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button
                    onClick={saveWritebackPermissions}
                    disabled={savingWritebackEvents}
                    className="mt-6 w-full bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                    {savingWritebackEvents ? 'Saving...' : 'Save Writeback Permissions'}
                </button>
            </div>

            {/* Dental Offices Permissions Section (from Legacy API office list) */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                    <h3 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                        <Building2 className="w-6 h-6 text-teal-600" />
                        Dental Office Permissions
                    </h3>
                    <div className="flex gap-2">
                        <button
                            onClick={selectAllOffices}
                            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition"
                        >
                            Select All
                        </button>
                        <button
                            onClick={deselectAllOffices}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                        >
                            Deselect All
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-3 mb-4">
                    <input
                        type="text"
                        placeholder="Search offices by ID, email, name, or mobile..."
                        value={officeSearchTerm}
                        onChange={(e) => setOfficeSearchTerm(e.target.value)}
                        className="flex-1 px-4 py-2.5 text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    {officeList.length > 0 && (
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">
                            {filteredOffices.length} office{filteredOffices.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                {officeListError && (
                    <p className="text-sm text-red-600">{officeListError}</p>
                )}

                {!officeListError && officeList.length === 0 && (
                    <p className="text-sm text-gray-500">No dental offices found.</p>
                )}

                {!officeListError && officeList.length > 0 && (
                    <>
                        <button
                            type="button"
                            onClick={() => setShowSelectedOnly(prev => !prev)}
                            className={`text-sm mb-3 rounded px-2 py-1 -ml-2 transition ${showSelectedOnly ? 'bg-teal-100 text-teal-700' : 'text-gray-600 hover:bg-gray-100'}`}
                        >
                            Selected Office{selectedOffices.length !== 1 ? 's' : ''}: <span className="font-semibold text-teal-600">{selectedOffices.length}</span> 
                            {showSelectedOnly ? ' (showing selected only — click to show all)' : ' (click to view)'}
                        </button>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="p-3 text-left font-semibold whitespace-nowrap w-10"></th>
                                        <th className="p-3 text-left font-semibold whitespace-nowrap">Office ID</th>
                                        <th className="p-3 text-left font-semibold whitespace-nowrap">Email</th>
                                        <th className="p-3 text-left font-semibold whitespace-nowrap">Office Name</th>
                                        <th className="p-3 text-left font-semibold whitespace-nowrap">Mobile</th>
                                        <th className="p-3 text-left font-semibold whitespace-nowrap">Administrator Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedOffices.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="p-4 text-center text-gray-500">
                                                {showSelectedOnly ? 'No offices selected yet.' : 'No offices match your search.'}
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedOffices.map((office, idx) => (
                                            <tr key={office.dentulu_office_id ?? idx} className="border-t">
                                                <td className="p-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedOffices.includes(String(office.dentulu_office_id))}
                                                        onChange={() => toggleOfficeSelection(office.dentulu_office_id)}
                                                        className="w-4 h-4"
                                                    />
                                                </td>
                                                <td className="p-3 whitespace-nowrap">{office.dentulu_office_id ?? '—'}</td>
                                                <td className="p-3 whitespace-nowrap">{office.email ?? '—'}</td>
                                                <td className="p-3 whitespace-nowrap">{office.office_name ?? '—'}</td>
                                                <td className="p-3 whitespace-nowrap">{office.mobile ?? '—'}</td>
                                                <td className="p-3 whitespace-nowrap">{office.administrator_name ?? '—'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {officeTotalPages > 1 && (
                            <div className="flex justify-center items-center space-x-2 mt-4">
                                <button
                                    onClick={() => setOfficePage(p => Math.max(1, p - 1))}
                                    disabled={officePage === 1}
                                    className="px-4 py-2.5 rounded-lg transition-colors font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>

                                <span className="text-sm text-gray-600">
                                    Page {officePage} of {officeTotalPages}
                                </span>

                                <button
                                    onClick={() => setOfficePage(p => Math.min(officeTotalPages, p + 1))}
                                    disabled={officePage === officeTotalPages}
                                    className="px-4 py-2.5 rounded-lg transition-colors font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        )}

                        <button
                            onClick={saveDentalOfficePermissions}
                            disabled={savingOffices}
                            className="mt-6 w-full bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                        >
                            {savingOffices ? 'Saving...' : 'Save Dental Office Permissions'}
                        </button>
                    </>
                )}
            </div>

            {/* User Apps Section (Display Only) */}
            {userApps.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
                            <Package className="w-6 h-6 text-gray-600" />
                            User Apps
                        </h3>
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {userApps.length} app{userApps.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {userApps.map(app => (
                            <div key={app.id} className="border rounded-lg p-4 flex items-center gap-3">
                                {app.icon_url ? (
                                    <img src={app.icon_url} alt="" className="w-12 h-12 rounded" />
                                ) : (
                                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                        <Package className="w-6 h-6 text-gray-500" />
                                    </div>
                                )}
                                <div>
                                    <div className="font-semibold text-gray-900">{app.name}</div>
                                    <div className="text-xs text-gray-500">
                                        {app.type === 'public' ? 'Marketplace' : formatRole(app.type)}
                                        {app.is_approved ? (
                                            <span className="ml-2 text-green-600">• Active</span>
                                        ) : (
                                            <span className="ml-2 text-yellow-600">• Pending</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* API Keys Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-2xl font-semibold mb-4 text-gray-900">API Keys</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="p-3 text-left font-semibold">Type</th>
                                <th className="p-3 text-left font-semibold">Key (partial)</th>
                                <th className="p-3 text-left font-semibold">Status</th>
                                <th className="p-3 text-left font-semibold">Limit</th>
                                <th className="p-3 text-left font-semibold">Usage</th>
                                <th className="p-3 text-left font-semibold">Created</th>
                                <th className="p-3 text-left font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {keys.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="p-4 text-center text-gray-500">
                                        No API keys found
                                    </td>
                                </tr>
                            ) : (
                                keys.map(k => (
                                    <tr key={k.id} className="border-t hover:bg-gray-50">
                                        <td className="p-3">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold ${k.type === 'production'
                                                    ? 'bg-purple-100 text-purple-700'
                                                    : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                {formatRole(k.type)}
                                            </span>
                                        </td>
                                        <td className="p-3 font-mono text-xs">{k.key.slice(0, 12)}...</td>
                                        <td className="p-3">
                                            {k.is_active ? (
                                                <span className="text-green-600 font-semibold">✓ Active</span>
                                            ) : (
                                                <span className="text-red-600 font-semibold">✗ Inactive</span>
                                            )}
                                        </td>
                                        <td className="p-3">{k.limit}</td>
                                        <td className="p-3">{k.usage_count}</td>
                                        <td className="p-3 text-gray-600">
                                            {new Date(k.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="p-3">
                                            <div className="flex gap-2">
                                                {k.type === 'production' && !k.is_active && (
                                                    <button
                                                        onClick={() => approveKey(k.id)}
                                                        className="text-green-600 hover:underline text-xs font-semibold"
                                                    >
                                                        Approve
                                                    </button>
                                                )}
                                                {k.is_active && (
                                                    <button
                                                        onClick={() => revokeKey(k.id)}
                                                        className="text-red-600 hover:underline text-xs font-semibold"
                                                    >
                                                        Revoke
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => rotateKey(k.id)}
                                                    className="text-blue-600 hover:underline text-xs font-semibold"
                                                >
                                                    Rotate
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Webhook Logs Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-2xl font-semibold mb-4 text-gray-900">Recent Webhook Logs</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="p-3 text-left font-semibold">Event</th>
                                <th className="p-3 text-left font-semibold">Target URL</th>
                                <th className="p-3 text-left font-semibold">Status</th>
                                <th className="p-3 text-left font-semibold">Timestamp</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="p-4 text-center text-gray-500">
                                        No webhook logs found
                                    </td>
                                </tr>
                            ) : (
                                logs.map(l => (
                                    <tr key={l.id} className="border-t hover:bg-gray-50">
                                        <td className="p-3 font-mono text-xs">{l.event_type}</td>
                                        <td className="p-3 truncate max-w-xs text-blue-600">{l.target_url}</td>
                                        <td className="p-3">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold ${l.status_code >= 200 && l.status_code < 300
                                                    ? 'bg-green-100 text-green-700'
                                                    : l.status_code >= 400
                                                        ? 'bg-red-100 text-red-700'
                                                        : 'bg-gray-100 text-gray-700'
                                                }`}>
                                                {l.status_code}
                                            </span>
                                        </td>
                                        <td className="p-3 text-gray-600 text-xs">
                                            {new Date(l.timestamp).toLocaleString()}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}